/*
 * 2-2.5 «Неделя» журнала. Геометрия из макета: шкала времени 56 px, строка получаса
 * 24 px, шапка дней прилипшая, сетка `56px repeat(7, 1fr)`.
 *
 * Ни одного цвета мимо токенов: ступени загрузки объявлены в journal-tokens.css,
 * остальное — общие токены кабинета. Тёмная тема поэтому работает сама, а это здесь
 * не мелочь: администратор смотрит журнал весь день, и шкала загрузки, не
 * перевёрнутая по светлоте, показала бы «дыру» там, где её нет.
 */

.wrap {
  --week-row: 24px;
  --week-time: 56px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
}

/*
 * ПРОКРУТКА — НА ОБЁРТКЕ, А НЕ НА САМОЙ СЕТКЕ.
 *
 * `overflow: hidden` на сетке отменял `position: sticky` у шапки дней: прокрутив
 * до вечерних часов, человек терял подписи дней и уже не знал, в каком дне находится.
 * Клип и радиус живут здесь, а сетка остаётся видимой целиком.
 *
 * `touch-action: pan-y` — чтобы палец прокручивал, а не считался попыткой тащить.
 */
.scroll {
  min-width: 0;
  max-height: min(70vh, 720px);
  overflow: auto;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  background: var(--bg-card);
  touch-action: pan-y;
}

.grid {
  display: grid;
  grid-template-columns: var(--week-time) repeat(7, minmax(0, 1fr));
  min-width: 0;
  background: var(--bg-card);
}

.corner {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--bg-card);
  border-bottom: 1px solid var(--border);
}

/* Шапка дней прилипает: неделя выше экрана, и без этого на середине прокрутки
   непонятно, в каком дне находишься. */
.head {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px 6px;
  min-height: 40px;
  background: var(--bg-card);
  border-bottom: 1px solid var(--border);
  border-left: 1px solid var(--border);
}

.headToday {
  composes: head;
  background: var(--note-bg);
  color: var(--note-fg);
}

.headDay {
  border: 0;
  padding: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  text-align: left;
  cursor: pointer;
}

.headDay:hover {
  text-decoration: underline;
}

.headSub {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
}

.headToday .headSub {
  color: inherit;
}

.time {
  grid-column: 1;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding-right: 6px;
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  /* Линия часа заметнее линии получаса: по ней глаз и отсчитывает время. */
  border-top: 1px solid transparent;
}

.time:not(:empty) {
  border-top-color: var(--border);
}

/* ── «Загрузка салона» ────────────────────────────────────────────────────── */

/*
 * Клетка — не кнопка.
 *
 * СЕМЬ СОТЕН КНОПОК В ПОРЯДКЕ ОБХОДА — ЭТО НЕ ДОСТУПНОСТЬ, А ЛОВУШКА: чтобы уйти
 * с недели на клавиатуре, надо нажать Tab сто шестьдесят восемь раз. Мышью клетка
 * по-прежнему открывает день, а с клавиатуры день открывается кнопкой в шапке дня —
 * одна цель на день вместо двадцати восьми.
 */
.cell {
  display: flex;
  align-items: center;
  justify-content: center;
  border-left: 1px solid var(--border);
  border-top: 1px solid var(--border);
  padding: 0;
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  line-height: 1;
  cursor: pointer;
  min-width: 0;
}

.tone-idle {
  composes: cell;
  background: var(--load-idle-bg);
  color: var(--load-idle-fg);
}

.tone-low {
  composes: cell;
  background: var(--load-low-bg);
  color: var(--load-low-fg);
}

.tone-mid {
  composes: cell;
  background: var(--load-mid-bg);
  color: var(--load-mid-fg);
}

.tone-high {
  composes: cell;
  background: var(--load-high-bg);
  color: var(--load-high-fg);
}

/*
 * «Не работает» — штриховка, а не серая заливка: серое читается как «занято»,
 * то есть ровно наоборот.
 *
 * ШАГ ТОТ ЖЕ, ЧТО У НЕРАБОЧЕГО ДНЯ В РЕЖИМЕ МАСТЕРА (4px/9px): два разных шага
 * для одного смысла читаются как два разных смысла. Мелкая штриховка 2px/5px
 * на клетке 24 px к тому же сливалась в ровный фон.
 */
.tone-off {
  composes: cell;
  background: repeating-linear-gradient(
    135deg,
    var(--load-off-hatch) 0 4px,
    transparent 4px 9px
  );
  color: transparent;
  cursor: default;
}

/* ── «Один мастер» ────────────────────────────────────────────────────────── */

.column {
  position: relative;
  border-left: 1px solid var(--border);
  min-width: 0;
}

/* Подложка дня: по ней открывается день, и она не мешает нажать карточку. */
.dayCanvas {
  position: absolute;
  inset: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.offDay {
  position: absolute;
  inset: 0;
  border: 0;
  cursor: pointer;
  background: repeating-linear-gradient(
    135deg,
    var(--load-off-hatch) 0 4px,
    transparent 4px 9px
  );
}

.card {
  position: absolute;
  left: 2px;
  right: 2px;
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  padding: 1px 4px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: var(--bg-card);
  color: inherit;
  font: inherit;
  font-size: 10.5px;
  line-height: 1.2;
  text-align: left;
  cursor: pointer;
}

/* Запись не на линии получаса: без пунктира её край не отличить от соседней клетки. */
.cardOffGrid {
  composes: card;
  border-style: dashed;
  border-color: var(--note-fg);
}

.cardName {
  font-weight: var(--font-weight-semibold);
}

/* ── Объяснение запрета перетаскивания ───────────────────────────────────── */

/*
 * Плашка лежит НАЛОЖЕНИЕМ. В потоке она сдвигала сетку вниз в тот самый миг, когда
 * человек ведёт мышь, — и клетка уезжала из-под курсора.
 */
.dragNote {
  position: absolute;
  left: 50%;
  top: var(--space-2);
  transform: translateX(-50%);
  z-index: 3;
  box-shadow: 0 6px 24px rgb(0 0 0 / 18%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  max-width: 560px;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  background: var(--note-plain-bg);
  color: var(--note-plain-fg);
  font-size: var(--font-size-sm);
}

.dragClose {
  flex: 0 0 auto;
  border: 1px solid currentColor;
  border-radius: var(--radius-sm);
  background: transparent;
  color: inherit;
  font: inherit;
  padding: 2px 10px;
  cursor: pointer;
}

.srOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

/* Отменённая запись — приглушённо и зачёркнуто, как в сетке дня: иначе мастер придёт
   к клиенту, который отменился. */
.cardCancelled {
  composes: card;
  opacity: 0.55;
  text-decoration: line-through;
}

/*
 * «Смены нет, а время занято»: ни штриховка (продавать нечего), ни светлое (можно
 * продать) здесь не годятся — оба ответа неверны. Рамка предупреждения говорит
 * «посмотрите этот день», и это ровно то, что надо сделать.
 */
.tone-outside {
  composes: cell;
  background: var(--note-bg);
  color: var(--note-fg);
  box-shadow: inset 0 0 0 1px var(--note-fg);
}
